import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2, Boxes, Calendar as CalIcon, Sparkles, Plus, Trash2,
  Loader2, AlertTriangle, Check, X, MapPin, Users, Send,
  List as ListIcon, LayoutGrid, ChevronLeft, ChevronRight,
  FileDown, Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/* -------------------------------------------------------------------------- */
/*  Events — rooms, assets, NL booking                                        */
/* -------------------------------------------------------------------------- */

function fmtRange(start, end) {
  try {
    const s = new Date(start);
    const e = new Date(end);
    const same = s.toDateString() === e.toDateString();
    const dateStr = s.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
    const sT = s.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    const eT = e.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return same ? `${dateStr} · ${sT}–${eT}` : `${s.toLocaleString()} → ${e.toLocaleString()}`;
  } catch { return `${start} → ${end}`; }
}

/* ─── Natural-language booking bar ─────────────────────────────────── */
function NLBar({ onBooked, rooms, assets }) {
  const [text, setText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const parse = async () => {
    if (!text.trim()) return;
    setParsing(true);
    setProposal(null);
    try {
      const tz = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC';
      const { data } = await api.post('/events/parse-booking', { request: text, tz });
      setProposal(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not parse that booking.');
    } finally { setParsing(false); }
  };

  const confirm = async () => {
    if (!proposal) return;
    setConfirming(true);
    try {
      const { data } = await api.post('/events/bookings', proposal.proposal);
      toast.success(`Booked: ${data.purpose}`);
      setProposal(null);
      setText('');
      onBooked?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Conflict — pick another slot.');
    } finally { setConfirming(false); }
  };

  return (
    <div className="ve-card p-5" data-testid="events-nl-bar">
      <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
        <Sparkles size={13} /> Book with natural language
      </div>
      <p className="text-xs text-slate-500 dark:text-white/55 mt-1">
        Tell Iqra what you need: <i>&ldquo;Book the boardroom Thursday 2-4pm with the projector for the Series A meeting.&rdquo;</i>
      </p>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && parse()}
          placeholder="Book ..."
          data-testid="events-nl-input"
          className="flex-1 rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5]"
        />
        <button
          onClick={parse}
          disabled={parsing || !text.trim()}
          data-testid="events-nl-parse"
          className="ve-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {parsing ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Parse
        </button>
      </div>

      {proposal && (
        <div className="mt-4 rounded-md border border-[#8B3FC5]/40 bg-[#FFF8E5]/50 dark:bg-[#3a2f0a]/40 p-4" data-testid="events-nl-proposal">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Proposed booking · confidence {proposal.confidence}%</div>
              <div className="text-sm font-bold mt-1">{proposal.proposal.purpose}</div>
              <div className="text-xs text-slate-600 dark:text-white/65 mt-1">{fmtRange(proposal.proposal.start, proposal.proposal.end)}</div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {proposal.room_name && (
                  <span className="inline-flex items-center gap-1 font-bold">
                    <Building2 size={11} className="text-[#5E1F8B] dark:text-[#8B3FC5]"/> {proposal.room_name}
                  </span>
                )}
                {proposal.asset_names?.length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Boxes size={11} className="text-[#5E1F8B] dark:text-[#8B3FC5]"/>
                    {proposal.asset_names.join(', ')}
                  </span>
                )}
                {proposal.proposal.attendees?.length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Users size={11}/>
                    {proposal.proposal.attendees.length} attendee(s)
                  </span>
                )}
              </div>
              {proposal.reasoning && (
                <p className="mt-2 text-[10px] italic text-slate-500 dark:text-white/55">{proposal.reasoning}</p>
              )}
              {proposal.conflicts?.length > 0 && (
                <div className="mt-3 rounded border border-rose-200 dark:border-rose-400/30 bg-rose-50 dark:bg-rose-900/15 p-2.5">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-rose-700 dark:text-rose-300 flex items-center gap-1">
                    <AlertTriangle size={10}/> {proposal.conflicts.length} conflict{proposal.conflicts.length === 1 ? '' : 's'}
                  </div>
                  {proposal.conflicts.slice(0, 3).map((c, i) => (
                    <div key={i} className="text-[11px] mt-1">
                      {c.kind === 'room' ? 'Room' : 'Asset'} clash: &ldquo;{c.booking?.purpose}&rdquo; {fmtRange(c.booking?.start, c.booking?.end)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={confirm}
                disabled={confirming || proposal.conflicts?.length > 0}
                data-testid="events-nl-confirm"
                className="ve-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {confirming ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>} Confirm
              </button>
              <button
                onClick={() => setProposal(null)}
                className="text-xs uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded border border-slate-200 dark:border-white/10 text-slate-500 inline-flex items-center gap-1.5"
              >
                <X size={11}/> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Room / Asset inline-add form ─────────────────────────────────── */
function InlineAddRoom({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [cap, setCap] = useState(10);
  const [equipment, setEquipment] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post('/events/rooms', {
        name: name.trim(),
        capacity: Number(cap) || 1,
        equipment: equipment.split(',').map((e) => e.trim()).filter(Boolean),
      });
      toast.success('Room added.');
      setOpen(false); setName(''); setEquipment(''); setCap(10);
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add.');
    } finally { setBusy(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="events-add-room" className="ve-btn-ghost text-xs inline-flex items-center gap-1.5">
      <Plus size={12}/> Add room
    </button>
  );
  return (
    <div className="ve-card p-3 flex flex-wrap items-center gap-2">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Boardroom" className="rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-2 py-1.5 text-xs flex-1 min-w-[160px] focus:outline-none focus:border-[#8B3FC5]"/>
      <input type="number" value={cap} onChange={(e) => setCap(e.target.value)} min={1} className="w-20 rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-2 py-1.5 text-xs focus:outline-none focus:border-[#8B3FC5]" placeholder="cap"/>
      <input value={equipment} onChange={(e) => setEquipment(e.target.value)} placeholder="projector, mic" className="rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-2 py-1.5 text-xs flex-1 min-w-[140px] focus:outline-none focus:border-[#8B3FC5]"/>
      <button onClick={submit} disabled={busy} className="ve-btn-primary text-xs">{busy ? '…' : 'Add'}</button>
      <button onClick={() => setOpen(false)} className="text-[10px] uppercase font-bold text-slate-400">×</button>
    </div>
  );
}

function InlineAddAsset({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState('equipment');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post('/events/assets', { name: name.trim(), kind, unit_count: 1 });
      toast.success('Asset added.');
      setOpen(false); setName(''); setKind('equipment');
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add.');
    } finally { setBusy(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="events-add-asset" className="ve-btn-ghost text-xs inline-flex items-center gap-1.5">
      <Plus size={12}/> Add asset
    </button>
  );
  return (
    <div className="ve-card p-3 flex flex-wrap items-center gap-2">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Projector" className="rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-2 py-1.5 text-xs flex-1 min-w-[160px] focus:outline-none focus:border-[#8B3FC5]"/>
      <select value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-2 py-1.5 text-xs">
        <option value="equipment">Equipment</option>
        <option value="vehicle">Vehicle</option>
        <option value="space">Space</option>
        <option value="other">Other</option>
      </select>
      <button onClick={submit} disabled={busy} className="ve-btn-primary text-xs">{busy ? '…' : 'Add'}</button>
      <button onClick={() => setOpen(false)} className="text-[10px] uppercase font-bold text-slate-400">×</button>
    </div>
  );
}

/* ─── Calendar week-grid view ──────────────────────────────────────── */
const WEEK_HOURS_START = 7;   // 7am
const WEEK_HOURS_END = 21;    // 9pm  → 14 row slots
const HOUR_PX = 44;           // tall enough to read 1-line bookings

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();              // 0 Sun … 6 Sat
  const diff = (day === 0 ? -6 : 1) - day; // ISO week — Monday first
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

/* ─── Quick-create modal (click or drag on the week grid) ─────────── */
/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on prop-sync */
function QuickCreateModal({ open, start, end, rooms, assets, onClose, onCreated }) {
  const [roomId, setRoomId] = useState(rooms[0]?.room_id || '');
  const [assetIds, setAssetIds] = useState([]);
  const [purpose, setPurpose] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setRoomId(rooms[0]?.room_id || '');
      setAssetIds([]);
      setPurpose('');
    }
  }, [open, rooms]);

  if (!open || !start || !end) return null;

  const startLabel = start.toLocaleString(undefined, { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  const endLabel = end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const durationMin = Math.round((end - start) / 60000);

  const submit = async () => {
    if (!purpose.trim()) {
      toast.error('Add a purpose first.');
      return;
    }
    if (!roomId && assetIds.length === 0) {
      toast.error('Pick a room or at least one asset.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/events/bookings', {
        room_id: roomId || null,
        asset_ids: assetIds,
        start: start.toISOString(),
        end: end.toISOString(),
        purpose: purpose.trim(),
        attendees: [],
        notes: '',
      });
      toast.success('Booked.');
      onCreated?.();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Could not create booking.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="quick-create-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="ve-card w-full max-w-md p-5 space-y-4"
        data-testid="quick-create-modal"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-1.5">
              <CalIcon size={11}/> Quick-create booking
            </div>
            <div className="mt-1 text-sm font-bold text-slate-800 dark:text-white/90">{startLabel} – {endLabel}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">{durationMin} min</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="quick-create-close"><X size={14}/></button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 dark:text-white/55">Purpose</label>
          <input
            type="text"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Series A pitch · IC pre-brief · standup …"
            autoFocus
            data-testid="quick-create-purpose"
            className="mt-1 w-full text-sm h-10 px-3 rounded-md border border-slate-200 dark:border-white/15 bg-transparent focus:outline-none focus:border-[#8B3FC5] text-slate-900 dark:text-white placeholder:text-slate-400"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 dark:text-white/55">Room</label>
          <select
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            data-testid="quick-create-room"
            className="mt-1 w-full text-sm h-10 px-3 rounded-md border border-slate-200 dark:border-white/15 bg-transparent focus:outline-none focus:border-[#8B3FC5] text-slate-900 dark:text-white"
          >
            <option value="">No room — assets only</option>
            {rooms.map((r) => (
              <option key={r.room_id} value={r.room_id}>{r.name} {r.capacity ? `(${r.capacity})` : ''}</option>
            ))}
          </select>
        </div>

        {assets.length > 0 && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 dark:text-white/55">Assets (optional)</label>
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="quick-create-assets">
              {assets.map((a) => {
                const selected = assetIds.includes(a.asset_id);
                return (
                  <button
                    key={a.asset_id}
                    type="button"
                    onClick={() => setAssetIds((ids) => selected ? ids.filter((x) => x !== a.asset_id) : [...ids, a.asset_id])}
                    data-testid={`quick-create-asset-${a.asset_id}`}
                    className={`px-2.5 h-7 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border transition ${selected ? 'border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]/40 text-[#5E1F8B] dark:text-[#8B3FC5]' : 'border-slate-200 dark:border-white/15 text-slate-500 hover:border-[#8B3FC5]'}`}
                  >
                    {a.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={submit}
            disabled={busy || !purpose.trim()}
            data-testid="quick-create-submit"
            className="inline-flex items-center gap-1.5 px-4 h-10 text-xs uppercase tracking-[0.2em] font-bold rounded-md bg-[#0A0A0A] text-[#8B3FC5] hover:bg-[#1a1a1a] disabled:opacity-40 transition"
          >
            {busy ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>}
            {busy ? 'Booking…' : 'Confirm'}
          </button>
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 h-10 text-xs uppercase tracking-[0.2em] font-bold rounded-md border border-slate-200 dark:border-white/15 text-slate-700 dark:text-white/70 hover:border-rose-400 hover:text-rose-500 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function WeekGrid({ bookings, rooms, weekStart, onPrev, onNext, onToday, onPickSlot, onCancel, onResize, onReschedule, selectMode = false, selected = new Set(), onToggleSelect }) {
  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      out.push(d);
    }
    return out;
  }, [weekStart]);
  const hours = useMemo(() => {
    const out = [];
    for (let h = WEEK_HOURS_START; h < WEEK_HOURS_END; h++) out.push(h);
    return out;
  }, []);
  const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.room_id, r])), [rooms]);

  // Bucket bookings by day-of-week (0-6, mon-sun)
  const byDay = useMemo(() => {
    const buckets = Array.from({ length: 7 }, () => []);
    bookings.forEach((b) => {
      const start = new Date(b.start);
      const dayIdx = days.findIndex((d) =>
        d.getFullYear() === start.getFullYear() &&
        d.getMonth() === start.getMonth() &&
        d.getDate() === start.getDate(),
      );
      if (dayIdx === -1) return;
      buckets[dayIdx].push(b);
    });
    return buckets;
  }, [bookings, days]);

  // Drag-to-create state (one per render — only one drag at a time)
  const [drag, setDrag] = useState(null); // { dayIdx, startMin, endMin }
  // Drag-to-resize state for an existing booking
  const [resizing, setResizing] = useState(null); // { booking, dayIdx, startMin, endMin }
  // Drag-to-reschedule — moves the whole block to a new day/time
  const [moving, setMoving] = useState(null); // { booking, originDayIdx, originStartMin, dayIdx, startMin, durationMin }

  const pxToMinutes = (px) => Math.round((px / HOUR_PX) * 60 / 15) * 15; // snap 15min

  const handleColMouseDown = (dayIdx, d, e) => {
    if (e.button !== 0) return; // primary mouse only
    const rect = e.currentTarget.getBoundingClientRect();
    const startMin = Math.max(0, pxToMinutes(e.clientY - rect.top));
    setDrag({ dayIdx, startMin, endMin: startMin + 60 });
    e.preventDefault();
  };
  const handleColMouseMove = (dayIdx, e) => {
    if (resizing && resizing.dayIdx === dayIdx) {
      const rect = e.currentTarget.getBoundingClientRect();
      const endMin = Math.max(resizing.startMin + 15, pxToMinutes(e.clientY - rect.top));
      setResizing({ ...resizing, endMin });
      return;
    }
    if (moving) {
      // Cross-column drag: detect which column we're hovering over
      const rect = e.currentTarget.getBoundingClientRect();
      const startMin = Math.max(0, pxToMinutes(e.clientY - rect.top));
      setMoving({ ...moving, dayIdx, startMin });
      return;
    }
    if (!drag || drag.dayIdx !== dayIdx) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const endMin = Math.max(drag.startMin + 15, pxToMinutes(e.clientY - rect.top));
    setDrag({ ...drag, endMin });
  };
  const handleColMouseUp = (dayIdx, d) => {
    if (resizing && resizing.dayIdx === dayIdx) {
      const r = resizing;
      const newEnd = new Date(d);
      newEnd.setHours(WEEK_HOURS_START, 0, 0, 0);
      newEnd.setMinutes(r.endMin);
      onResize?.(r.booking, newEnd.toISOString());
      setResizing(null);
      return;
    }
    if (moving) {
      const m = moving;
      // Only commit if the user actually moved somewhere different.
      const targetDay = days[m.dayIdx] || d;
      const newStart = new Date(targetDay);
      newStart.setHours(WEEK_HOURS_START, 0, 0, 0);
      newStart.setMinutes(m.startMin);
      const newEnd = new Date(newStart.getTime() + m.durationMin * 60000);
      // No-op if the new start matches the original (within 1 min)
      const originalStart = new Date(m.booking.start);
      if (Math.abs(newStart - originalStart) > 60000) {
        onReschedule?.(m.booking, newStart.toISOString(), newEnd.toISOString());
      }
      setMoving(null);
      return;
    }
    if (!drag || drag.dayIdx !== dayIdx) return;
    const isClick = drag.endMin - drag.startMin < 15;
    const startMin = drag.startMin;
    // Single-click (no drag) → default 60 min; otherwise clamp at min 30.
    const endMin = isClick ? Math.min(startMin + 60, (WEEK_HOURS_END - WEEK_HOURS_START) * 60) : Math.max(drag.startMin + 30, drag.endMin);
    const start = new Date(d);
    start.setHours(WEEK_HOURS_START, 0, 0, 0);
    start.setMinutes(startMin);
    const end = new Date(d);
    end.setHours(WEEK_HOURS_START, 0, 0, 0);
    end.setMinutes(endMin);
    setDrag(null);
    onPickSlot?.(start, end);
  };

  // If the mouse exits the grid mid-drag, cancel cleanly.
  useEffect(() => {
    const stop = () => { setDrag(null); setResizing(null); setMoving(null); };
    window.addEventListener('mouseup', stop, { passive: true });
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const todayKey = new Date().toDateString();
  const weekLabel = `${days[0].toLocaleDateString(undefined, { day: '2-digit', month: 'short' })} – ${days[6].toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}`;

  return (
    <div className="ve-card p-3" data-testid="events-week-grid">
      <div className="flex items-center justify-between gap-2 px-1 pb-2 mb-2 border-b border-slate-200 dark:border-white/10">
        <div className="flex items-center gap-2">
          <button onClick={onPrev} data-testid="week-prev" className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-200 dark:border-white/10 hover:border-[#8B3FC5]"><ChevronLeft size={14}/></button>
          <button onClick={onToday} data-testid="week-today" className="h-8 px-3 text-[10px] uppercase tracking-[0.18em] font-bold rounded border border-slate-200 dark:border-white/10 hover:border-[#8B3FC5]">Today</button>
          <button onClick={onNext} data-testid="week-next" className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-200 dark:border-white/10 hover:border-[#8B3FC5]"><ChevronRight size={14}/></button>
        </div>
        <div className="text-xs font-bold text-slate-700 dark:text-white/85 tabular-nums" data-testid="week-label">{weekLabel}</div>
      </div>

      {/* 8-column grid: 1 narrow hour column + 7 day columns */}
      <div className="grid" style={{ gridTemplateColumns: `48px repeat(7, minmax(0, 1fr))` }}>
        {/* Header row: day names */}
        <div />
        {days.map((d, i) => {
          const isToday = d.toDateString() === todayKey;
          return (
            <div
              key={i}
              className={`text-center pb-2 text-[10px] uppercase tracking-[0.16em] font-bold ${isToday ? 'text-[#5E1F8B] dark:text-[#8B3FC5]' : 'text-slate-500 dark:text-white/55'}`}
              data-testid={`week-day-header-${i}`}
            >
              <div>{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
              <div className={`mt-0.5 text-base font-bold ${isToday ? 'text-[#8B3FC5]' : 'text-slate-700 dark:text-white/80'}`}>
                {d.getDate()}
              </div>
            </div>
          );
        })}

        {/* Body row: 1 cell + 7 day columns with hour rows + absolute booking blocks */}
        <div className="border-t border-slate-200 dark:border-white/10">
          {hours.map((h) => (
            <div
              key={h}
              className="h-[44px] text-[10px] text-slate-400 dark:text-white/40 pr-1 text-right tabular-nums leading-none pt-0.5"
            >
              {h.toString().padStart(2, '0')}:00
            </div>
          ))}
        </div>
        {days.map((d, dayIdx) => {
          const isDraggingHere = drag?.dayIdx === dayIdx;
          const isResizingHere = resizing?.dayIdx === dayIdx;
          const ghost = isDraggingHere ? { startMin: Math.min(drag.startMin, drag.endMin), endMin: Math.max(drag.startMin, drag.endMin) } : null;
          return (
            <div
              key={dayIdx}
              className="relative border-t border-l border-slate-200 dark:border-white/10 select-none"
              data-testid={`week-col-${dayIdx}`}
              onMouseDown={(e) => handleColMouseDown(dayIdx, d, e)}
              onMouseMove={(e) => handleColMouseMove(dayIdx, e)}
              onMouseUp={() => handleColMouseUp(dayIdx, d)}
            >
              {/* Hour gridlines */}
              {hours.map((h) => (
                <div
                  key={h}
                  className="h-[44px] border-b border-slate-100 dark:border-white/5 hover:bg-amber-50/40 dark:hover:bg-amber-900/10 transition"
                  title={`Click or drag from ${d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' })} ${h.toString().padStart(2, '0')}:00`}
                  data-testid={`week-empty-${dayIdx}-${h}`}
                />
              ))}

              {/* Drag-create ghost */}
              {ghost && (
                <div
                  className="absolute left-[3px] right-[3px] rounded bg-[#8B3FC5]/30 border-2 border-dashed border-[#8B3FC5] pointer-events-none"
                  style={{
                    top: `${(ghost.startMin / 60) * HOUR_PX}px`,
                    height: `${Math.max(20, ((ghost.endMin - ghost.startMin) / 60) * HOUR_PX)}px`,
                  }}
                  data-testid={`week-drag-ghost-${dayIdx}`}
                >
                  <div className="text-[10px] font-bold text-[#7A5A00] dark:text-[#8B3FC5] px-1.5 pt-1 leading-tight">
                    {Math.floor(ghost.startMin / 60) + WEEK_HOURS_START}:{(ghost.startMin % 60).toString().padStart(2, '0')}
                    {' → '}
                    {Math.floor(ghost.endMin / 60) + WEEK_HOURS_START}:{(ghost.endMin % 60).toString().padStart(2, '0')}
                  </div>
                </div>
              )}

              {/* Move-ghost: shows where the booking will land while reschedule-dragging */}
              {moving && moving.dayIdx === dayIdx && (
                <div
                  className="absolute left-[3px] right-[3px] rounded bg-[#8B3FC5]/40 border-2 border-[#8B3FC5] pointer-events-none"
                  style={{
                    top: `${(moving.startMin / 60) * HOUR_PX}px`,
                    height: `${Math.max(22, (moving.durationMin / 60) * HOUR_PX - 2)}px`,
                  }}
                  data-testid={`week-move-ghost-${dayIdx}`}
                >
                  <div className="text-[10px] font-bold text-[#7A5A00] dark:text-[#8B3FC5] px-1.5 pt-1 leading-tight truncate">
                    {moving.booking.purpose}
                  </div>
                  <div className="text-[9px] text-[#7A5A00]/80 dark:text-[#8B3FC5]/80 px-1.5 leading-tight">
                    Move to {Math.floor(moving.startMin / 60) + WEEK_HOURS_START}:{(moving.startMin % 60).toString().padStart(2, '0')}
                  </div>
                </div>
              )}

              {/* Booking blocks for this day */}
              {byDay[dayIdx].map((b) => {
                const start = new Date(b.start);
                const end = new Date(b.end);
                const minutesFromGridStart = Math.max(0, (start.getHours() - WEEK_HOURS_START) * 60 + start.getMinutes());
                let durationMin = Math.max(20, Math.round((end - start) / 60000));
                // If currently resizing this booking, use the live ghost length.
                if (isResizingHere && resizing.booking.booking_id === b.booking_id) {
                  durationMin = Math.max(20, resizing.endMin - minutesFromGridStart);
                }
                durationMin = Math.min(durationMin, (WEEK_HOURS_END - WEEK_HOURS_START) * 60);
                const top = (minutesFromGridStart / 60) * HOUR_PX;
                const height = Math.max(22, (durationMin / 60) * HOUR_PX - 2);
                const room = roomById[b.room_id];
                return (
                  <div
                    key={b.booking_id}
                    className={`absolute left-[3px] right-[3px] rounded ${selectMode && selected.has(b.booking_id) ? 'bg-[#8B3FC5]/30 ring-2 ring-[#8B3FC5]' : 'bg-[#FFF8E5] dark:bg-[#3a2f0a]'} border-l-[3px] border-[#8B3FC5] px-1.5 py-1 overflow-hidden hover:z-10 hover:shadow-md transition group ${moving?.booking?.booking_id === b.booking_id ? 'opacity-40' : ''} ${selectMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
                    style={{ top: `${top}px`, height: `${height}px` }}
                    data-testid={`week-booking-${b.booking_id}`}
                    title={selectMode ? `Click to ${selected.has(b.booking_id) ? 'deselect' : 'select'} · ${b.purpose}` : `Drag to move · ${b.purpose} · ${start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}–${end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}${room ? ' · ' + room.name : ''}`}
                    onClick={(e) => {
                      if (selectMode) {
                        e.stopPropagation();
                        onToggleSelect?.(b.booking_id);
                      }
                    }}
                    onMouseDown={(e) => {
                      if (selectMode) {
                        // Stop the column from also starting a drag-create.
                        e.stopPropagation();
                        return;
                      }
                      // Initiate drag-to-reschedule unless the user grabbed
                      // the resize handle or the cancel button.
                      if (e.target.closest('[data-resize-handle]') || e.target.closest('button')) return;
                      e.stopPropagation();
                      setMoving({
                        booking: b,
                        originDayIdx: dayIdx,
                        originStartMin: minutesFromGridStart,
                        dayIdx,
                        startMin: minutesFromGridStart,
                        durationMin,
                      });
                    }}
                  >
                    <div className="text-[10px] font-bold text-slate-800 dark:text-white/90 truncate leading-tight">
                      {b.purpose}
                    </div>
                    {height >= 36 && (
                      <div className="text-[9px] text-slate-500 dark:text-white/60 truncate leading-tight mt-0.5">
                        {start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        {room ? ` · ${room.name}` : ''}
                      </div>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); onCancel?.(b.booking_id); }}
                      className="absolute right-0.5 top-0.5 opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700 transition"
                      title="Cancel"
                      data-testid={`week-booking-cancel-${b.booking_id}`}
                    >
                      <X size={11}/>
                    </button>
                    {/* Resize handle at the bottom */}
                    <div
                      data-resize-handle="true"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setResizing({
                          booking: b,
                          dayIdx,
                          startMin: minutesFromGridStart,
                          endMin: minutesFromGridStart + durationMin,
                        });
                      }}
                      className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize bg-transparent hover:bg-[#8B3FC5]/40 transition"
                      title="Drag to resize"
                      data-testid={`week-booking-resize-${b.booking_id}`}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Main page ────────────────────────────────────────────────────── */
/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + websocket effects. */
export default function Events() {
  const [rooms, setRooms] = useState([]);
  const [assets, setAssets] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState('calendar');
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('ve_events_view') || 'list'; } catch { return 'list'; }
  });
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [freshIds, setFreshIds] = useState(new Set());
  const [livePolling, setLivePolling] = useState(true);
  // Quick-create modal state — opened by clicking or drag-creating on the week grid.
  const [quickCreate, setQuickCreate] = useState({ open: false, start: null, end: null });
  // Bulk-select mode: a Set of booking_ids currently selected on the week grid.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const loadAll = async (opts = {}) => {
    try {
      const [r, a, b] = await Promise.all([
        api.get('/events/rooms').then(({ data }) => data),
        api.get('/events/assets').then(({ data }) => data),
        api.get('/events/bookings').then(({ data }) => data),
      ]);
      // Detect newly-arrived bookings vs the previous render — gold-flash them.
      if (opts.silent) {
        setBookings((prev) => {
          const prevIds = new Set(prev.map((p) => p.booking_id));
          const added = b.filter((x) => !prevIds.has(x.booking_id)).map((x) => x.booking_id);
          if (added.length) {
            setFreshIds((f) => new Set([...f, ...added]));
            // Clear the flash after 2s so it doesn't loop on subsequent polls.
            setTimeout(() => {
              setFreshIds((f) => {
                const next = new Set(f);
                added.forEach((id) => next.delete(id));
                return next;
              });
            }, 2000);
          }
          return b;
        });
      } else {
        setBookings(b);
      }
      setRooms(r); setAssets(a);
    } catch (e) {
      if (!opts.silent) toast.error(e?.response?.data?.detail || 'Could not load events.');
    }
  };
  useEffect(() => { loadAll(); }, []);

  // Live WebSocket push — instant booking updates from other users.
  useEffect(() => {
    if (!livePolling) return undefined;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');
    if (!base) return undefined;
    // Cookie-first auth — same-origin WS handshake sends session_token automatically.
    const ws = new WebSocket(`${base}/api/events/ws`);
    let pingTimer = null;
    ws.onopen = () => {
      pingTimer = setInterval(() => { try { ws.send('ping'); } catch { /* no-op */ } }, 25000);
    };
    ws.onmessage = (evt) => {
      let payload;
      try { payload = JSON.parse(evt.data); } catch { return; }
      if (payload?.type === 'booking.created' || payload?.type === 'booking.deleted') {
        // Silent reload — the diff-flash logic in loadAll handles the gold pulse.
        loadAll({ silent: true });
      }
    };
    ws.onerror = () => { /* fail silently — poll fallback covers us */ };
    return () => {
      if (pingTimer) clearInterval(pingTimer);
      try { ws.close(); } catch { /* no-op */ }
    };
  }, [livePolling]);

  // Auto-poll every 30s as a fallback — multi-user bookings still appear
  // even if the WebSocket is blocked by the network.
  useEffect(() => {
    if (!livePolling) return undefined;
    const t = setInterval(() => loadAll({ silent: true }), 30000);
    return () => clearInterval(t);
  }, [livePolling]);

  const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.room_id, r])), [rooms]);
  const assetById = useMemo(() => Object.fromEntries(assets.map((a) => [a.asset_id, a])), [assets]);

  const deleteRoom = async (id) => {
    if (!window.confirm('Delete this room? Bookings on it will be orphaned.')) return;
    await api.delete(`/events/rooms/${id}`);
    toast.success('Deleted.'); loadAll();
  };
  const deleteAsset = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    await api.delete(`/events/assets/${id}`);
    toast.success('Deleted.'); loadAll();
  };
  const cancelBooking = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    await api.delete(`/events/bookings/${id}`);
    toast.success('Cancelled.'); loadAll();
  };
  const repeatBooking = async (id) => {
    try {
      const { data } = await api.post(`/events/bookings/${id}/repeat?weeks_ahead=1`);
      toast.success(`Booked again ${new Date(data.start).toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' })}`);
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Could not repeat — slot conflicts.';
      toast.error(msg);
    }
  };

  // ─── Bulk actions on the week grid ───────────────────────────────────
  const toggleSelectMode = () => {
    setSelectMode((m) => {
      if (m) setSelected(new Set());
      return !m;
    });
  };
  const toggleSelected = (booking_id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(booking_id)) next.delete(booking_id); else next.add(booking_id);
      return next;
    });
  };
  const bulkCancel = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Cancel ${ids.length} booking${ids.length === 1 ? '' : 's'}?`)) return;
    try {
      const { data } = await api.post('/events/bookings/bulk', { booking_ids: ids, action: 'cancel' });
      toast.success(`${data.cancelled} cancelled.`);
      setSelected(new Set());
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk cancel failed.');
    }
  };
  const bulkShift = async (days) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const { data } = await api.post('/events/bookings/bulk', { booking_ids: ids, action: 'shift_days', days });
      const moved = data.moved?.length || 0;
      const skipped = data.skipped?.length || 0;
      toast.success(`${moved} moved${skipped ? ` · ${skipped} skipped (conflicts)` : ''}.`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk shift failed.');
    }
  };

  // ─── ICS export + print ─────────────────────────────────────────────
  const exportIcs = async () => {
    const { bookingsToIcs, downloadIcs } = await import('@/lib/ics');
    // Limit to the visible week if we're on Week view, else everything.
    let target = bookings;
    if (view === 'week') {
      const wkEnd = new Date(weekStart); wkEnd.setDate(wkEnd.getDate() + 7);
      target = bookings.filter((b) => {
        const t = new Date(b.start);
        return t >= weekStart && t < wkEnd;
      });
    }
    if (!target.length) {
      toast.error('No bookings to export.');
      return;
    }
    const roomById = Object.fromEntries(rooms.map((r) => [r.room_id, r]));
    const ics = bookingsToIcs(target, { roomById, calendarName: 'Vigilant Entities · Bookings' });
    const filename = view === 'week'
      ? `vigilant-week-${weekStart.toISOString().slice(0, 10)}.ics`
      : `vigilant-all-bookings-${new Date().toISOString().slice(0, 10)}.ics`;
    downloadIcs(filename, ics);
    toast.success(`Exported ${target.length} bookings.`);
  };
  const printWeek = () => {
    document.body.classList.add('ve-printing-events');
    window.print();
    // remove the class shortly after to allow the print dialog to settle
    setTimeout(() => document.body.classList.remove('ve-printing-events'), 1500);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8 space-y-6" data-testid="events-page">
      <header>
        <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
          <CalIcon size={13}/> Events · rooms · assets
          <button
            onClick={() => setLivePolling((v) => !v)}
            data-testid="events-live-toggle"
            className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-400/30 text-emerald-700 dark:text-emerald-300 text-[9px] uppercase tracking-[0.2em] font-bold hover:bg-emerald-100"
            title={livePolling ? 'Live (auto-refreshes every 30s) — click to pause' : 'Paused — click to resume live updates'}
          >
            {livePolling && <span className="ve-live-dot" />}
            {livePolling ? 'Live' : 'Paused'}
          </button>
        </div>
        <h1 className="display text-3xl sm:text-4xl mt-2 font-bold">Book anything with a sentence.</h1>
        <p className="text-sm text-slate-600 dark:text-white/65 mt-2 max-w-2xl">
          Rooms, assets, and bookings live in one place. Iqra parses natural-language requests, flags conflicts before you confirm, and writes the calendar entry.
        </p>
      </header>

      <NLBar rooms={rooms} assets={assets} onBooked={loadAll}/>

      <div className="flex gap-1 border-b border-slate-200 dark:border-white/10">
        {[
          { id: 'calendar', label: 'Calendar', icon: CalIcon, count: bookings.length },
          { id: 'rooms',    label: 'Rooms',    icon: Building2, count: rooms.length },
          { id: 'assets',   label: 'Assets',   icon: Boxes, count: assets.length },
        ].map((t) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`events-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold border-b-2 -mb-px transition ${isActive ? 'border-[#8B3FC5] text-slate-900 dark:text-white' : 'border-transparent text-slate-500 dark:text-white/55 hover:text-slate-700'}`}
            >
              <t.icon size={11}/> {t.label}
              <span className="text-[10px] font-bold opacity-60">· {t.count}</span>
            </button>
          );
        })}
      </div>

      {tab === 'calendar' && (
        <div className="space-y-2" data-testid="events-calendar">
          {/* View toggle: list vs week-grid */}
          <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="events-view-toggle">
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setView('list'); try { localStorage.setItem('ve_events_view', 'list'); } catch { /* no-op */ } }}
                data-testid="events-view-list"
                aria-selected={view === 'list'}
                className={`inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border transition ${view === 'list' ? 'border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]/40 text-[#5E1F8B] dark:text-[#8B3FC5]' : 'border-slate-200 dark:border-white/10 text-slate-500 hover:border-[#8B3FC5]'}`}
              >
                <ListIcon size={11}/> List
              </button>
              <button
                onClick={() => { setView('week'); try { localStorage.setItem('ve_events_view', 'week'); } catch { /* no-op */ } }}
                data-testid="events-view-week"
                aria-selected={view === 'week'}
                className={`inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border transition ${view === 'week' ? 'border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]/40 text-[#5E1F8B] dark:text-[#8B3FC5]' : 'border-slate-200 dark:border-white/10 text-slate-500 hover:border-[#8B3FC5]'}`}
              >
                <LayoutGrid size={11}/> Week
              </button>
            </div>
            <div className="flex items-center gap-1">
              {view === 'week' && (
                <button
                  onClick={toggleSelectMode}
                  data-testid="events-select-toggle"
                  aria-pressed={selectMode}
                  className={`inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border transition ${selectMode ? 'border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]/40 text-[#5E1F8B] dark:text-[#8B3FC5]' : 'border-slate-200 dark:border-white/10 text-slate-500 hover:border-[#8B3FC5]'}`}
                  title={selectMode ? 'Exit select mode' : 'Multi-select bookings'}
                >
                  <Check size={11}/> {selectMode ? `Selecting (${selected.size})` : 'Select'}
                </button>
              )}
              <button
                onClick={exportIcs}
                data-testid="events-export-ics"
                className="inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border border-slate-200 dark:border-white/10 text-slate-500 hover:border-[#8B3FC5] hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] transition"
                title="Download as .ics (Outlook / Apple Calendar / Google Calendar)"
              >
                <FileDown size={11}/> Export ICS
              </button>
              {view === 'week' && (
                <button
                  onClick={printWeek}
                  data-testid="events-print"
                  className="inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border border-slate-200 dark:border-white/10 text-slate-500 hover:border-[#8B3FC5] hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] transition"
                  title="Print the visible week"
                >
                  <Printer size={11}/> Print
                </button>
              )}
            </div>
          </div>

          {/* Bulk action bar — visible when select-mode is on and ≥1 selected */}
          {selectMode && selected.size > 0 && (
            <div
              className="ve-card p-3 flex items-center justify-between flex-wrap gap-2 border-l-4 border-[#8B3FC5]"
              data-testid="events-bulk-bar"
            >
              <div className="text-xs font-bold text-slate-800 dark:text-white/90">
                {selected.size} booking{selected.size === 1 ? '' : 's'} selected
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => bulkShift(-7)}
                  data-testid="events-bulk-prev-week"
                  className="inline-flex items-center gap-1 px-2.5 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border border-slate-200 dark:border-white/10 hover:border-[#8B3FC5]"
                >
                  <ChevronLeft size={11}/> -1 wk
                </button>
                <button
                  onClick={() => bulkShift(7)}
                  data-testid="events-bulk-next-week"
                  className="inline-flex items-center gap-1 px-2.5 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border border-slate-200 dark:border-white/10 hover:border-[#8B3FC5]"
                >
                  +1 wk <ChevronRight size={11}/>
                </button>
                <button
                  onClick={bulkCancel}
                  data-testid="events-bulk-cancel"
                  className="inline-flex items-center gap-1 px-2.5 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md bg-rose-600 text-white hover:bg-rose-700"
                >
                  <Trash2 size={11}/> Cancel all
                </button>
                <button
                  onClick={() => setSelected(new Set())}
                  data-testid="events-bulk-clear"
                  className="inline-flex items-center gap-1 px-2.5 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border border-slate-200 dark:border-white/10 hover:border-rose-400 hover:text-rose-500"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {view === 'week' && (
            <WeekGrid
              bookings={bookings}
              rooms={rooms}
              weekStart={weekStart}
              selectMode={selectMode}
              selected={selected}
              onToggleSelect={toggleSelected}
              onPrev={() => setWeekStart((d) => { const x = new Date(d); x.setDate(x.getDate() - 7); return x; })}
              onNext={() => setWeekStart((d) => { const x = new Date(d); x.setDate(x.getDate() + 7); return x; })}
              onToday={() => setWeekStart(startOfWeek(new Date()))}
              onCancel={cancelBooking}
              onPickSlot={(start, end) => setQuickCreate({ open: true, start, end })}
              onResize={async (b, newEndIso) => {
                try {
                  await api.patch(`/events/bookings/${b.booking_id}`, { end: newEndIso });
                  toast.success('Resized.');
                  loadAll({ silent: true });
                } catch (e) {
                  const detail = e?.response?.data?.detail;
                  const msg = typeof detail === 'string' ? detail : detail?.message || 'Could not resize.';
                  toast.error(msg);
                }
              }}
              onReschedule={async (b, newStartIso, newEndIso) => {
                try {
                  await api.patch(`/events/bookings/${b.booking_id}`, { start: newStartIso, end: newEndIso });
                  toast.success('Rescheduled.', { duration: 4500, id: `rescheduled-${b.booking_id}` });
                  loadAll({ silent: true });
                } catch (e) {
                  const detail = e?.response?.data?.detail;
                  const msg = typeof detail === 'string' ? detail : detail?.message || 'Could not move — conflicts.';
                  toast.error(msg);
                }
              }}
            />
          )}

          {view === 'list' && bookings.length === 0 && (
            <div className="ve-card p-6 text-center text-sm text-slate-500 dark:text-white/55">
              No bookings yet. Use the natural-language bar above to create one.
            </div>
          )}
          {view === 'list' && bookings.map((b, i) => {
            const room = roomById[b.room_id];
            const isFresh = freshIds.has(b.booking_id);
            return (
              <div
                key={b.booking_id}
                className={`ve-card p-4 flex items-start gap-3 ${isFresh ? 've-row-flash' : ''}`}
                style={{ animation: !isFresh ? `ve-pillar-in 0.35s ease ${Math.min(i, 8) * 40}ms both` : undefined }}
                data-testid={`booking-${b.booking_id}`}
              >
                <div className="h-10 w-10 rounded-md bg-[#FFF8E5] dark:bg-[#3a2f0a] flex items-center justify-center shrink-0">
                  <CalIcon size={16} className="text-[#5E1F8B] dark:text-[#8B3FC5]"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold">{b.purpose}</h3>
                    <span className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                      {b.status || 'confirmed'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-white/55 mt-1">{fmtRange(b.start, b.end)}</div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600 dark:text-white/65">
                    {room && <span className="inline-flex items-center gap-1"><MapPin size={10}/> {room.name}</span>}
                    {(b.asset_ids || []).length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <Boxes size={10}/>
                        {b.asset_ids.map((id) => assetById[id]?.name || id).join(', ')}
                      </span>
                    )}
                    {(b.attendees || []).length > 0 && (
                      <span className="inline-flex items-center gap-1"><Users size={10}/> {b.attendees.length}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button
                    onClick={() => repeatBooking(b.booking_id)}
                    data-testid={`booking-repeat-${b.booking_id}`}
                    className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] hover:text-[#8B3FC5] inline-flex items-center gap-1"
                    title="Book this again at the same time next week"
                  >
                    <Sparkles size={10}/> Repeat +1w
                  </button>
                  <button onClick={() => cancelBooking(b.booking_id)} data-testid={`booking-cancel-${b.booking_id}`} className="text-[10px] uppercase tracking-[0.22em] font-bold text-rose-600 hover:text-rose-700">Cancel</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'rooms' && (
        <div className="space-y-3" data-testid="events-rooms-tab">
          <InlineAddRoom onAdded={loadAll}/>
          {rooms.length === 0 && (
            <div className="ve-card p-6 text-center text-sm text-slate-500 dark:text-white/55">
              No rooms yet. Add one above.
            </div>
          )}
          {rooms.map((r) => (
            <div key={r.room_id} className="ve-card p-4 flex items-start gap-3" data-testid={`room-${r.room_id}`}>
              <div className="h-10 w-10 rounded-md bg-[#FFF8E5] dark:bg-[#3a2f0a] flex items-center justify-center shrink-0">
                <Building2 size={16} className="text-[#5E1F8B] dark:text-[#8B3FC5]"/>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold">{r.name}</h3>
                <div className="text-xs text-slate-500 dark:text-white/55 mt-0.5">
                  Capacity {r.capacity} {r.location ? `· ${r.location}` : ''}
                </div>
                {r.equipment?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {r.equipment.map((e) => (
                      <span key={e} className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded bg-slate-100 dark:bg-white/5">{e}</span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => deleteRoom(r.room_id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      )}

      {tab === 'assets' && (
        <div className="space-y-3" data-testid="events-assets-tab">
          <InlineAddAsset onAdded={loadAll}/>
          {assets.length === 0 && (
            <div className="ve-card p-6 text-center text-sm text-slate-500 dark:text-white/55">
              No assets yet. Add one above.
            </div>
          )}
          {assets.map((a) => (
            <div key={a.asset_id} className="ve-card p-4 flex items-start gap-3" data-testid={`asset-${a.asset_id}`}>
              <div className="h-10 w-10 rounded-md bg-[#FFF8E5] dark:bg-[#3a2f0a] flex items-center justify-center shrink-0">
                <Boxes size={16} className="text-[#5E1F8B] dark:text-[#8B3FC5]"/>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold">{a.name}</h3>
                <div className="text-xs text-slate-500 dark:text-white/55 mt-0.5 capitalize">{a.kind} · {a.unit_count} unit{a.unit_count === 1 ? '' : 's'}</div>
              </div>
              <button onClick={() => deleteAsset(a.asset_id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      )}

      <QuickCreateModal
        open={quickCreate.open}
        start={quickCreate.start}
        end={quickCreate.end}
        rooms={rooms}
        assets={assets}
        onClose={() => setQuickCreate({ open: false, start: null, end: null })}
        onCreated={() => loadAll()}
      />
    </div>
  );
}
